import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { prefersReducedMotion } from '../utils/motion';

const HeroSection = styled.section`
  position: relative;
  width: 100%;
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0a2540;
`;

const VideoBackground = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
  opacity: 0.7;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(10,37,64,0.7) 0%, rgba(10,37,64,0.3) 100%);
  z-index: 2;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
  color: #fff;
  padding: 4rem 2rem;
`;

const HeroTitleWrapper = styled.div`
  margin-bottom: 2.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 16px rgba(0,0,0,0.3);
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  position: relative;
  flex-wrap: nowrap;
  white-space: nowrap;
  @media (max-width: 600px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    white-space: normal;
    word-break: break-word;
    flex-wrap: wrap;
    text-align: center;
  }
`;

// Removed blinking cursor to avoid the trailing "|" after the hero title

const CTAButton = styled.button`
  background: linear-gradient(135deg, var(--primary-orange) 0%, #ff6b35 100%);
  color: white;
  border: none;
  margin-top: 2rem;
  padding: 18px 40px;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(236, 92, 44, 0.3);
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.8s ease-out 0.4s both;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(236, 92, 44, 0.4);
    &::before {
      left: 100%;
    }
  }
  &:active {
    transform: translateY(-1px);
  }
  @media (max-width: 768px) {
    padding: 15px 30px;
    font-size: 1.1rem;
  }
`;

const Hero: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Typewriter for the localized hero title (runs on initial mount)
  const fullTitle = useMemo(() => String(t('hero.title')), [t, i18n.language]);
  const [typed, setTyped] = useState<string>('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // run only once on first mount
    startedRef.current = true;

    if (prefersReducedMotion()) {
      setTyped(fullTitle);
      return;
    }

    let i = 0;
    let cancelled = false;
    const baseDelay = 45; // ms per character
    const tick = () => {
      if (cancelled) return;
      i += 1;
      setTyped(fullTitle.slice(0, i));
      if (i < fullTitle.length) {
        const jitter = Math.random() * 40; // small human-like variance
        setTimeout(tick, baseDelay + jitter);
      }
    };
    setTimeout(tick, 250); // small initial delay
    return () => {
      cancelled = true;
    };
  }, [fullTitle]);

    const handleCTAClick = () => {
        // Scroll to contact form or handle trial lesson booking
        const contactSection = document.getElementById('demo-lesson');
        if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

  return (
    <HeroSection id="home">
      <VideoBackground
        autoPlay
        loop
        muted
        playsInline
        poster="/assets/h1_hero.png"
      >
        <source src="/assets/uni-drive.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </VideoBackground>
      <Overlay />
      <HeroContent>
        {/* Visually hidden image for accessibility and test compliance */}
        <img src="/assets/car.png" alt="Car" style={{width: 1, height: 1, position: 'absolute', left: '-9999px', top: 'auto'}} />
        <HeroTitleWrapper>
          <HeroTitle aria-label={fullTitle}>
            <span aria-hidden="true" style={{display: 'inline-block'}}>{typed || fullTitle}</span>
          </HeroTitle>
        </HeroTitleWrapper>
        <div>
          <CTAButton onClick={e => { e.preventDefault(); handleCTAClick(); }}>
            {t('intro.button')}
          </CTAButton>
        </div>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero;